// Tiny hash-based router (offline-friendly, no server rewrites needed).
export type RouteHandler = (params: Record<string, string>, query: URLSearchParams) => void | Promise<void>;

interface Route { pattern: RegExp; keys: string[]; handler: RouteHandler; }

const routes: Route[] = [];
let notFound: RouteHandler = () => {};
let beforeEach: (path: string) => string | null = () => null; // return redirect path or null

export function compile(path: string): { pattern: RegExp; keys: string[] } {
  const keys: string[] = [];
  const pattern = new RegExp(
    '^' + path.replace(/:[^/]+/g, (m) => { keys.push(m.slice(1)); return '([^/]+)'; }) + '$',
  );
  return { pattern, keys };
}

interface Router {
  add(path: string, handler: RouteHandler): Router;
  setNotFound(h: RouteHandler): void;
  setBeforeEach(fn: (path: string) => string | null): void;
  navigate(path: string): void;
  start(): void;
  current(): string;
}

export const router: Router = {
  add(path: string, handler: RouteHandler): Router {
    const { pattern, keys } = compile(path);
    routes.push({ pattern, keys, handler });
    return router;
  },
  setNotFound(h: RouteHandler): void { notFound = h; },
  setBeforeEach(fn: (path: string) => string | null): void { beforeEach = fn; },
  navigate(path: string): void {
    if (location.hash.slice(1) === path) { resolve(); return; }
    location.hash = path;
  },
  start(): void {
    window.addEventListener('hashchange', resolve);
    resolve();
  },
  current(): string { return location.hash.slice(1) || '/'; },
};

async function resolve(): Promise<void> {
  let raw = location.hash.slice(1) || '/';
  const redirect = beforeEach(raw);
  if (redirect && redirect !== raw) { location.hash = redirect; return; }
  const [path, qs] = raw.split('?');
  const query = new URLSearchParams(qs || '');
  for (const route of routes) {
    const m = path.match(route.pattern);
    if (m) {
      const params: Record<string, string> = {};
      route.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1])));
      await route.handler(params, query);
      window.scrollTo(0, 0);
      return;
    }
  }
  await notFound({}, query);
}
